// Bookable add-ons ("Zusätze"): the catalog on /prices (GET /AddOn/GetAll, hooks/useAddOns), the sign-up add-ons of
// a process (EventProcessAddOn rows, all events of the process share them) and the booked lines of a participant
// (EventParticipantResponse.addOns). Mirror of the backend rules (AveOrga.Core/Services/AddOnRules.cs,
// AddOn.SettingsAt / GroupPrice, EventProcessAddOn.PriceFor, AddOnValidator) — the server stays authoritative;
// change both sides together.
//
// Two booking channels per line (AddOnChannel, enums arrive as strings):
// - Signup: pre-booked at sign-up (homepage, inbox, add form, edit popup) — part of the participant price,
//   counts against the max, may exist on waitlist rows;
// - OnSite: sold in the event ("Artikel") — not part of the price, only for present participants.
// Capacity (maxQuantity, 0 = unlimited) counts the quantity of all lines of both channels of non-deleted,
// non-waitlist participants (pending approvals count).
import { formatEuro } from './payment';
import { colorVar, PARTICIPANT_GROUP_COLOR_KEYS } from './participantGroups';

export const ADD_ON_NAME_MAX = 200;
export const ADD_ON_DESCRIPTION_MAX = 1000;
export const ADD_ON_MAIL_HINT_MAX = 1000;
export const ADD_ON_PRICE_MAX = 10000;
export const ADD_ON_MAX_QUANTITY_MAX = 10000;
export const ADD_ON_MAX_PER_PERSON_MAX = 99;
export const ADD_ON_PLACEHOLDER_KEY_MAX = 30;

export const ADD_ON_CHANNEL = Object.freeze({ signup: 'Signup', onSite: 'OnSite' });

// Tolerant: 'OnSite' / 1 → 'OnSite', everything else (incl. missing) → 'Signup'.
export function normalizeAddOnChannel(channel) {
  return channel === ADD_ON_CHANNEL.onSite || channel === 1 || channel === '1'
    ? ADD_ON_CHANNEL.onSite
    : ADD_ON_CHANNEL.signup;
}

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const toNumberOrNull = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toCount = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : fallback;
};

const byName = (a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'de', { sensitivity: 'base' });

// ── Color ───────────────────────────────────────────────────────────────────────────────────────

// Palette of an add-on (AddOn.ColorKey, mirror of the AddOnValidator list): the participant group palette without
// warning/negative — yellow stays "ausgebucht", red stays errors. 'neutral' = "Ohne Farbe" (the plain pill).
export const ADD_ON_COLOR_KEYS = PARTICIPANT_GROUP_COLOR_KEYS;
const ADD_ON_COLOR_KEY_SET = new Set(ADD_ON_COLOR_KEYS);

// Tolerant: unknown, reserved (warning/negative) or missing keys (old offline copies, deleted catalog entries,
// statistics rows grouped by name) → 'neutral'.
export const addOnColorKey = (key) => {
  const k = typeof key === 'string' ? key.trim() : '';
  return ADD_ON_COLOR_KEY_SET.has(k) ? k : 'neutral';
};

// Has the add-on an own color (anything but "Ohne Farbe")?
export const hasAddOnColor = (key) => addOnColorKey(key) !== 'neutral';

// Inline style of a tinted add-on element (chip, counter pill, catalog icon): the custom property the CSS tint rules
// read (AddOnChips.css) — undefined without an own color, so the element keeps its neutral look.
export const addOnColorStyle = (key) =>
  (hasAddOnColor(key) ? { '--add-on-chip-color': colorVar(addOnColorKey(key)) } : undefined);

// ── Catalog ─────────────────────────────────────────────────────────────────────────────────────

// AddOnResponse → one tolerant shape (numbers, booleans, sorted lists). Normalizing twice is harmless.
export function normalizeAddOn(raw) {
  if (!raw || raw.id == null) return null;
  const locationSettings = (Array.isArray(raw.locationSettings) ? raw.locationSettings : [])
    .filter(s => s && s.locationId != null)
    .map(s => ({
      locationId: Number(s.locationId),
      locationName: s.locationName ?? '',
      price: toNumberOrNull(s.price),
      maxQuantity: toCount(s.maxQuantity),
      bookableOnHomepage: s.bookableOnHomepage === true,
      bookableOnSite: s.bookableOnSite === true,
    }))
    .sort((a, b) => a.locationName.localeCompare(b.locationName, 'de', { sensitivity: 'base' }));
  const participantGroupPrices = (Array.isArray(raw.participantGroupPrices) ? raw.participantGroupPrices : [])
    .filter(p => p && p.participantGroupId != null && toNumberOrNull(p.price) != null)
    .map(p => ({ participantGroupId: Number(p.participantGroupId), price: Number(p.price) }));
  const key = typeof raw.placeholderKey === 'string' ? raw.placeholderKey.trim() : '';
  return {
    ...raw,
    id: Number(raw.id),
    name: raw.name ?? '',
    description: raw.description || null,
    colorKey: addOnColorKey(raw.colorKey),
    maxPerPerson: Math.max(1, toCount(raw.maxPerPerson, 1)),
    waitlistWhenFull: raw.waitlistWhenFull === true,
    placeholderKey: key || null,
    mailHint: raw.mailHint || null,
    price: toNumberOrNull(raw.price),
    maxQuantity: toCount(raw.maxQuantity),
    bookableOnHomepage: raw.bookableOnHomepage === true,
    bookableOnSite: raw.bookableOnSite === true,
    locationSettings,
    participantGroupPrices,
    templateCount: toCount(raw.templateCount),
    processCount: toCount(raw.processCount),
    placeholderUsage: (Array.isArray(raw.placeholderUsage) ? raw.placeholderUsage : []).filter(Boolean).map(String),
  };
}

// Catalog order = name (the server sends the DB collation order; local patches keep it).
export function normalizeAddOns(list) {
  return sortAddOnsByName((Array.isArray(list) ? list : []).map(normalizeAddOn).filter(Boolean));
}

export const sortAddOnsByName = (list) => [...list].sort(byName);

// Referenced by a process template or a process (delete → 409).
export const isAddOnUsed = (addOn) => Number(addOn?.templateCount ?? 0) + Number(addOn?.processCount ?? 0) > 0;

// Textblock names using one of the generated placeholders (key change/clear and delete → 409).
export const addOnPlaceholderUsage = (addOn) => (Array.isArray(addOn?.placeholderUsage) ? addOn.placeholderUsage : []);

// Catalog values at a location — mirror of AddOn.SettingsAt: the own setting of that location, else the standard;
// null = "entfällt" there (not offered on site, not copied into new processes).
// → { price, maxQuantity, bookableOnHomepage, bookableOnSite, locationSetting (dto | null) } | null
export function settingsAt(addOn, locationId) {
  if (!addOn) return null;
  const locationSetting = locationId == null || !Array.isArray(addOn.locationSettings)
    ? null
    : addOn.locationSettings.find(s => sameId(s.locationId, locationId)) ?? null;
  const source = locationSetting ?? addOn;
  const price = toNumberOrNull(source.price);
  if (price == null) return null;
  return {
    price,
    maxQuantity: toCount(source.maxQuantity),
    bookableOnHomepage: source.bookableOnHomepage === true,
    bookableOnSite: source.bookableOnSite === true,
    locationSetting,
  };
}

// Fixed group price of a catalog entry, template row or process row (participantGroupPrices); null = none.
export function groupPrice(addOnOrRow, participantGroupId) {
  if (participantGroupId == null || !Array.isArray(addOnOrRow?.participantGroupPrices)) return null;
  const hit = addOnOrRow.participantGroupPrices.find(p => sameId(p.participantGroupId, participantGroupId));
  return hit ? toNumberOrNull(hit.price) : null;
}

// Price of a process row ({ price, participantGroupPrices }) for a group — mirror of EventProcessAddOn.PriceFor:
// the group price, else the row price. null only when the row has no price at all.
export function priceFor(row, participantGroupId) {
  const fixed = groupPrice(row, participantGroupId);
  return fixed != null ? fixed : toNumberOrNull(row?.price);
}

// Unit price of a new sign-up line (rule 3): event leaders pay 0, everyone else the row price of their group.
export const signupUnitPrice = (row, participantGroupId, isOrganizing = false) =>
  (isOrganizing ? 0 : (priceFor(row, participantGroupId) ?? 0));

// Live on-site price (rule R4): group price of the catalog entry, else the catalog price at the event location;
// null = not offered there.
export function onSitePrice(addOn, locationId, participantGroupId) {
  const s = settingsAt(addOn, locationId);
  if (!s) return null;
  const fixed = groupPrice(addOn, participantGroupId);
  return fixed != null ? fixed : s.price;
}

// Locations (from /Location/GetAll) without an own setting yet — choices for "+ Abweichung".
export function locationsWithoutAddOnSetting(settings, locations) {
  const taken = new Set((Array.isArray(settings) ? settings : []).map(s => String(s.locationId)));
  return (Array.isArray(locations) ? locations : []).filter(l => !taken.has(String(l.id)));
}

// ── Participant lines ────────────────────────────────────────────────────────────────────────────

const isWaitlistRow = (p) => p?.participantType === 'Waitlist' || p?.participantType === 3;
const isDeletedRow = (p) => p?.deletedAt != null;

const linesOf = (p) => (Array.isArray(p?.addOns) ? p.addOns : []);
const lineQuantity = (line) => toCount(line?.quantity);

// Booked lines of a participant per channel (EventParticipantResponse.addOns: ParticipantAddOnInfo[]).
export const signupLines = (p) => linesOf(p).filter(l => normalizeAddOnChannel(l.channel) === ADD_ON_CHANNEL.signup);
export const onSiteLines = (p) => linesOf(p).filter(l => normalizeAddOnChannel(l.channel) === ADD_ON_CHANNEL.onSite);

// Σ unitPrice × quantity of a participant's lines (on-site lines = "Gesamt Artikel").
export const linesTotal = (lines) =>
  (Array.isArray(lines) ? lines : []).reduce((sum, l) => sum + Number(l?.unitPrice ?? 0) * lineQuantity(l), 0);

// Taken quantity of an add-on at an event (rule 5 / R5): all lines of both channels on non-deleted, non-waitlist
// participants; `excludeId` leaves out one participant (its own current quantity when checking it).
export function taken(participants, addOnId, excludeId = null) {
  let sum = 0;
  (Array.isArray(participants) ? participants : []).forEach(p => {
    if (!p || isDeletedRow(p) || isWaitlistRow(p)) return;
    if (excludeId != null && sameId(p.id, excludeId)) return;
    linesOf(p).forEach(l => { if (sameId(l.addOnId, addOnId)) sum += lineQuantity(l); });
  });
  return sum;
}

// Taken quantity of every add-on at once → Map addOnId (number) → quantity (header counters).
export function takenByAddOn(participants) {
  const result = new Map();
  (Array.isArray(participants) ? participants : []).forEach(p => {
    if (!p || isDeletedRow(p) || isWaitlistRow(p)) return;
    linesOf(p).forEach(l => {
      if (l?.addOnId == null) return;
      const id = Number(l.addOnId);
      result.set(id, (result.get(id) ?? 0) + lineQuantity(l));
    });
  });
  return result;
}

// Still bookable quantity (R5): Infinity when unlimited (max 0), never below 0.
export function available(maxQuantity, takenQuantity) {
  const max = toCount(maxQuantity);
  if (max === 0) return Infinity;
  return Math.max(0, max - toCount(takenQuantity));
}

// IsExhausted(max, taken, requested) = max > 0 && taken + requested > max (requested 1 = "one more fits?").
export function isExhausted(maxQuantity, takenQuantity, requested = 1) {
  const max = toCount(maxQuantity);
  return max > 0 && toCount(takenQuantity) + toCount(requested) > max;
}

// New or increased sign-up quantities are clamped to 0…maxPerPerson; an existing higher quantity (`current`,
// e.g. after lowering the catalog limit) may stay or decrease (R8).
export function clampAddOnQuantity(quantity, maxPerPerson, current = 0) {
  const limit = Math.max(1, toCount(maxPerPerson, 1), toCount(current));
  const n = Math.trunc(Number(quantity));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, limit);
}

// ── Display ──────────────────────────────────────────────────────────────────────────────────────

// "Leihausrüstung", "BBs ×2".
export const formatAddOnQty = (name, quantity) => {
  const qty = toCount(quantity, 1);
  return qty > 1 ? `${name ?? ''} ×${qty}` : `${name ?? ''}`;
};

// Max per event: "max. 8" / "unbegrenzt" (0).
export const formatMax = (maxQuantity) => {
  const max = toCount(maxQuantity);
  return max > 0 ? `max. ${max}` : 'unbegrenzt';
};

// Counter "5/8" — only the count when unlimited ("5").
export const formatCapacity = (used, maxQuantity) => {
  const max = toCount(maxQuantity);
  return max > 0 ? `${toCount(used)}/${max}` : `${toCount(used)}`;
};

// Price of an add-on: "15,00 €", "kostenlos" (0) or "entfällt" (null).
export function formatAddOnPrice(price) {
  const n = toNumberOrNull(price);
  if (n == null) return 'entfällt';
  return n === 0 ? 'kostenlos' : formatEuro(n);
}

// "Mitglied kostenlos · Ehrenmitglied 5,00 €" — group prices in group display order (groups = the list from
// useParticipantGroups().groups; unknown groups are skipped). '' without group prices.
export function formatGroupPrices(addOnOrRow, groups) {
  const prices = Array.isArray(addOnOrRow?.participantGroupPrices) ? addOnOrRow.participantGroupPrices : [];
  if (prices.length === 0 || !Array.isArray(groups)) return '';
  return groups
    .map(g => ({ g, price: groupPrice(addOnOrRow, g.id) }))
    .filter(x => x.price != null)
    .map(x => `${x.g.name} ${formatAddOnPrice(x.price)}`)
    .join(' · ');
}

// Chips of booked lines (AddOnChips items): [{ key, label, colorKey }] — "Leihausrüstung", "BBs ×2"; colorKey = the
// current catalog color of the line's add-on (ParticipantAddOnInfo / RegistrationAddOnInfo.colorKey, 'neutral'
// without catalog entry).
export const addOnLineItems = (lines) =>
  (Array.isArray(lines) ? lines : []).map((l, index) => ({
    key: `line-${l.id ?? `${l.addOnId ?? 'x'}-${index}`}`,
    label: formatAddOnQty(l.name, l.quantity),
    colorKey: addOnColorKey(l.colorKey),
  }));

// Counter chips of an event's sign-up add-ons (AddOnChips items) — "Leihausrüstung 5/8", warning tone when
// exhausted (the warning tone wins over the add-on color). addOns: [{ addOnId, name, maxQuantity, used, colorKey }]
// (EventAddOnSummary / EventAddOnInfo / EventAddOnCapacity); usedById (Map | object, optional) overrides `used`,
// e.g. computed live with takenByAddOn().
export function addOnCounterItems(addOns, usedById = null) {
  const usedOf = (a) => {
    if (usedById instanceof Map) return usedById.get(Number(a.addOnId)) ?? 0;
    if (usedById && typeof usedById === 'object') return usedById[a.addOnId] ?? 0;
    return a.used ?? 0;
  };
  return (Array.isArray(addOns) ? addOns : []).map(a => {
    const used = toCount(usedOf(a));
    const max = toCount(a.maxQuantity);
    return {
      key: `addon-${a.addOnId}`,
      label: `${a.name ?? ''} ${formatCapacity(used, max)}`,
      tone: isExhausted(max, used) ? 'warning' : 'neutral',
      colorKey: addOnColorKey(a.colorKey),
      tooltip: max > 0
        ? `${a.name ?? ''}: ${used} von ${max} gebucht${used >= max ? ' – ausgebucht' : ''}`
        : `${a.name ?? ''}: ${used} gebucht (unbegrenzt)`,
    };
  });
}

// ── Mail placeholders (A5) ───────────────────────────────────────────────────────────────────────

const PLACEHOLDER_KEY_PATTERN = /^[A-Z][A-Za-z0-9]{0,29}$/;

// Placeholders the TextblockService resolves itself (without braces) — a generated token must not equal one.
export const STATIC_PLACEHOLDER_NAMES = Object.freeze([
  'Vorname', 'Nachname', 'Name', 'Email', 'Datum', 'Spieltag', 'Von', 'Bis', 'Ort', 'OrtAdresse', 'OrtKartenlink',
  'Gruppe', 'PreisEintritt', 'PreisEintrittGruppe', 'Betrag', 'KontaktdatenOrga', 'TageVorEvent', 'ResponseLink',
  'NewsletterSubscribeLink', 'NewsletterUnsubscribeLink', 'AusgewaehlteSpieltage', 'PersoenlicherLink',
  'TemporaeresPasswort', 'Code', 'Zusaetze', 'Zusatz',
]);

// Generic add-on placeholders (textblock editor; {Zusatz} only for the slot equipment_waitlist).
export const ADD_ON_STATIC_PLACEHOLDERS = Object.freeze([
  { key: '{Zusaetze}', label: 'Gebuchte Anmelde-Zusätze des Empfängers, z. B. „Leihausrüstung, BBs × 2“ – leer ohne Zusätze' },
  { key: '{Zusatz}', label: 'Ausgebuchte Zusätze, wegen denen der Empfänger auf der Zusatz-Warteliste steht – nur Mail „Zusatz-Warteliste“', slotOnly: true },
]);

const placeholderNames = (key) => [`Preis${key}`, `Preis${key}Gruppe`, `${key}Hinweis`];

const formatOk = (key) => PLACEHOLDER_KEY_PATTERN.test(key);

// Generated placeholders of a key: ['{PreisLeihe}', '{PreisLeiheGruppe}', '{LeiheHinweis}'] — [] for an empty or
// malformed key.
export function placeholderTokens(key) {
  const k = String(key ?? '').trim();
  if (!k || !formatOk(k)) return [];
  return placeholderNames(k).map(name => `{${name}}`);
}

// Generated placeholders with a description (key preview of the catalog popup, textblock editor).
export function placeholderTokenEntries(key, addOnName) {
  const tokens = placeholderTokens(key);
  if (tokens.length === 0) return [];
  const name = `„${addOnName || 'Zusatz'}“`;
  return [
    { key: tokens[0], label: `Preis von ${name} für Gäste im Prozess des Spieltags; ohne Spieltag-Bezug der Standardpreis. Bietet der Prozess den Zusatz nicht an, steht dort „–“` },
    { key: tokens[1], label: `Preis von ${name} für die Gruppe des Empfängers; sonst wie ${tokens[0]}` },
    { key: tokens[2], label: `Hinweistext von ${name} – nur für Teilnehmer (nicht auf der Warteliste), die den Zusatz gebucht haben; sonst leer` },
  ];
}

// Client check of a placeholder key (mirror of AddOnValidator, A5) → German message or null (valid; '' = no key).
// otherKeys: keys of the other catalog entries.
export function validatePlaceholderKey(key, otherKeys = []) {
  const k = String(key ?? '').trim();
  if (!k) return null;
  if (k.length > ADD_ON_PLACEHOLDER_KEY_MAX) {
    return `Das Kürzel darf höchstens ${ADD_ON_PLACEHOLDER_KEY_MAX} Zeichen lang sein.`;
  }
  if (!formatOk(k)) {
    return 'Das Kürzel muss mit einem Großbuchstaben beginnen und darf nur Buchstaben ohne Umlaute und Ziffern enthalten.';
  }
  if (/^eintritt/i.test(k)) return 'Das Kürzel darf nicht mit „Eintritt“ beginnen.';
  if (/(gruppe|hinweis)$/i.test(k)) return 'Das Kürzel darf nicht auf „Gruppe“ oder „Hinweis“ enden.';

  const lower = k.toLowerCase();
  const others = (Array.isArray(otherKeys) ? otherKeys : [])
    .map(o => String(o ?? '').trim())
    .filter(Boolean);
  if (others.some(o => o.toLowerCase() === lower)) return 'Dieses Kürzel verwendet schon ein anderer Zusatz.';

  const names = placeholderNames(k);
  const staticNames = new Set(STATIC_PLACEHOLDER_NAMES.map(n => n.toLowerCase()));
  const clashStatic = names.find(n => staticNames.has(n.toLowerCase()));
  if (clashStatic) return `Den Platzhalter {${clashStatic}} gibt es schon – bitte ein anderes Kürzel wählen.`;

  for (const other of others) {
    const otherNames = new Set(placeholderNames(other).map(n => n.toLowerCase()));
    const clash = names.find(n => otherNames.has(n.toLowerCase()));
    if (clash) return `Der Platzhalter {${clash}} entsteht schon aus dem Kürzel „${other}“.`;
  }
  return null;
}

// API error text for display (plain-text 400/409 bodies); never renders a JSON body.
export function addOnApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.title) return data.title;
  if (!err?.response) return 'Keine Verbindung – bitte später erneut versuchen.';
  if (err.response.status === 403) return 'Keine Berechtigung – nur Admins können Zusätze verwalten.';
  return fallback;
}
